import React, { useState, useMemo } from 'react'
import { Button, Stack, TextField, Link, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import SchoolIcon from '@mui/icons-material/School'
import { SchoolInfo } from '../types'
import CollapsibleSection from './CollapsibleSection'
import DataGridTable from './DataGridTable'
import LicenseModal from './LicenseModal'

interface SchoolsSectionProps {
  schools: SchoolInfo[]
  parentOrigin: string
  actionLoading: boolean
  postAction: (action: string, body?: Record<string, unknown>) => void
}

function SchoolsSection({
  schools,
  parentOrigin,
  actionLoading,
  postAction,
}: SchoolsSectionProps) {
  const [vCode, setVCode] = useState('')
  const [addSchoolOpen, setAddSchoolOpen] = useState(false)

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        width: 160,
        renderCell: (params) => (
          <Link
            href={`${parentOrigin}/school?v=${params.value}`}
            target='_blank'
            underline='hover'
          >
            {params.value}
          </Link>
        ),
      },
      { field: 'name', headerName: 'Name', flex: 1 },
    ],
    [parentOrigin]
  )

  const rows = schools.map((s) => ({ id: s.code, ...s }))

  const handleAdd = () => {
    postAction('addSchool', { vCode })
    setVCode('')
    setAddSchoolOpen(false)
  }

  return (
    <>
      <CollapsibleSection
        title='Schools'
        count={schools.length}
        icon={SchoolIcon}
        onAdd={() => {
          setAddSchoolOpen(true)
        }}
      >
        {rows.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            There are no schools.
          </Typography>
        ) : (
          <DataGridTable
            rows={rows}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
          />
        )}
      </CollapsibleSection>

      <LicenseModal
        open={addSchoolOpen}
        onClose={() => setAddSchoolOpen(false)}
        title='Add School'
        primaryLabel='Add School'
        onPrimary={handleAdd}
        primaryDisabled={actionLoading || !vCode}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size='small'
            label='School Code (vCode)'
            value={vCode}
            onChange={(e) => setVCode(e.target.value)}
            fullWidth
          />
        </Stack>
      </LicenseModal>
    </>
  )
}

export default React.memo(SchoolsSection)
